import { QueryKeys } from "@/constants";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import httpClient from "@/services";
import type { ServiceListResponse } from "@/model/serviceDetails";
import type { IBasePayload } from "@/model/base-payload";
import useUserInfo from "./use-user-info";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useIsFeatureEnabled } from "./use-features";
import ScreenRouteNames from "@/constants/screen-route-names";

interface ServiceRequestData extends IBasePayload {
  loginGroupId: string;
  accessToken: string;
}

export const useMemberService = (): UseQueryResult<
  ServiceListResponse,
  Error
> => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const requestData = {
    loginGroupId: userInfo?.loginGroupId,
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    accessToken: userInfo.accessToken,
    username: userInfo.username,
    planId: activeCoverage?.planId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
  };

  const isServicesFeatureEnabled = useIsFeatureEnabled(
    ScreenRouteNames.SERVICE.path,
  );

  return useQuery<ServiceListResponse, Error>({
    queryKey: [
      QueryKeys.MEMBER_SERVICE,
      userInfo.username,
      activeCoverage.claimCoverageCode,
    ],
    queryFn: () => fetchMemberServiceBenefitDetails(requestData),
    enabled:
      !!userInfo?.username &&
      !!userInfo?.accessToken &&
      isServicesFeatureEnabled,
    retry: 1,
  });
};

export const fetchMemberServiceBenefitDetails = async (
  info: ServiceRequestData,
) => {
  const url = "/service/serviceDetails";

  const params = {
    loginGroupId: info.loginGroupId,
    memberId: info.memberId,
    groupId: info.groupId,
    divisionId: info.divisionId,
    planId: info.planId,
    claimCoverageCode: info.claimCoverageCode,
  };

  const config = {
    headers: {
      Authorization: `Bearer ${info.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "3.0",
    },
  };

  return httpClient.post(url, params, config).then((res) => res.data?.results);
};
